import { useMemo, useState } from 'react';
import { Box, Card, alpha, useMediaQuery, Theme, CardHeader, Divider, CardContent } from '@mui/material';
import { useIntl } from 'react-intl';
import { PageContainer } from 'components/UI';
import { UncompletedTaskGroups } from 'components/card-widgets';
import { INavigationTabOption, useTabNavigation } from 'hooks';

import clsx from 'clsx';
import { useKycProvider } from 'providers/kyc';
import SimpleBar from 'simplebar-react';
import { makeStyles } from '@mui/styles';
import { PrimaryMarketTab } from './PrimaryMarketTab';
import { SecondaryMarketTab } from './SecondaryMarketTab';

const useStyles = makeStyles((theme) => ({
  pageContainer: {
    position: 'relative',
    gap: '10px',
  },

  card: {
    '&.MuiPaper-page': {
      position: 'relative',
      height: '100%',
      borderWidth: 0,

      [theme.breakpoints.up('sm')]: {
        borderWidth: '1px',
      },
    },

    '&.with-content-above': {
      height: '85vh',
    },
  },

  cardContentWrapper: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },

  header: {
    margin: '0 0 24px',
    paddingBottom: '8px',
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.dark,

    [theme.breakpoints.up('sm')]: {
      margin: '24px',
      paddingBottom: '0',
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
    },

    [theme.breakpoints.down('sm')]: {
      '& .MuiTabs-root': {
        '& .MuiTabs-indicator': {
          display: 'none',
        },

        '& .MuiTab-root': {
          '&.Mui-selected': {
            color: theme.palette.common.white,
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
          },

          '&.Mui-disabled': {
            color: alpha(theme.palette.common.white, 0.38),
          },
        },
      },
    },
  },

  content: {
    margin: '32px 16px',

    [theme.breakpoints.up('sm')]: {
      margin: '24px',
    },
  },
}));

enum EPortfolioTab {
  Primary = 'primary',
  Secondary = 'secondary',
  Solidarity = 'solidarity'
}

export const PortfolioPage = () => {
  const intl = useIntl();
  const classes = useStyles();
  const kycProvider = useKycProvider();

  const [simpleBarRef, setSimpleBarRef] = useState<SimpleBar | null>(null);

  const tabNavigationOptions = useMemo((): INavigationTabOption<EPortfolioTab>[] => {
    return [
      {
        name: EPortfolioTab.Primary,
        component: <PrimaryMarketTab wrapperRef={simpleBarRef?.getScrollElement()} />,
        tabLabel: intl.formatMessage({ id: 'portfolio.primary' }),
        urlPath: 'primary',
      },
      {
        name: EPortfolioTab.Secondary,
        component: <SecondaryMarketTab wrapperRef={simpleBarRef?.getScrollElement()} />,
        tabLabel: intl.formatMessage({ id: 'portfolio.secondary' }),
        urlPath: 'secondary',
      },
      // {
      //   name: EPortfolioTab.Solidarity,
      //   disabled: true,
      //   tabLabel: intl.formatMessage({ id: 'portfolio.solidarity' }),
      //   urlPath: 'solidarity',
      // },
    ];
  }, [intl, simpleBarRef]);

  const { tabsComponent, routeNavigationComponent } = useTabNavigation(tabNavigationOptions);

  const isTabletView = useMediaQuery<Theme>((theme) => theme.breakpoints.up('sm'));
  const withUncompletedTaskGroups = kycProvider.uncompletedTaskGroups.length > 0;

  return (
    <PageContainer>
      <UncompletedTaskGroups wrapper={(body) => <Box marginBottom="24px">{body}</Box>} />

      <Card
        variant="page"
        className={clsx(classes.card, { 'with-content-above': withUncompletedTaskGroups })}
      >
        <SimpleBar className={classes.cardContentWrapper} ref={setSimpleBarRef}>
          <CardHeader
            className={classes.header}
            title={
              <>
                <Box display="flex" justifyContent="flex-start">
                  {tabsComponent}
                </Box>

                {isTabletView && <Divider />}
              </>
            }
          />
          <CardContent className={classes.content}>{routeNavigationComponent}</CardContent>
        </SimpleBar>
      </Card>
    </PageContainer >
  );
};
